'use client';

import { TrailerDialogComponent } from '@/components/dialogs/trailer-dialog/trailer-dialog';
import { PlayButton } from '@/components/play-button/play-button';
import { imageMediaViewPorts } from '@/constants/image';
import { movieShowtimesTheaterLimits } from '@/constants/theater-search';
import { useMovieShowtimes } from '@/hooks/showtimes/movie-showtimes';
import { useSharedState } from '@/hooks/state/shared-state';
import { MoviePoster } from '@/types/movie-poster';
import { getTodayInTimeZone, parseDateString } from '@/utils/date-time-helper';
import { buildPreFirstShowtimeMessage, buildPresaleMessage } from '@/utils/presale-helpers';
import {
  filterActiveTheaterShowtimeGroups,
  hasAnyActiveTheaterShowtime,
  isActiveStatus,
  selectEligibleMovieShowtimeTheater,
} from '@/utils/showtime-filters';
import { Media, MovieTrailer, Theater } from '@cinemark/content-api-client';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

type MovieMediaItemComponentProps = {
  dataLinkCategory: string;
  dataLinkName?: string;
  largeImageUrl?: string;
  moviePoster: MoviePoster;
  movieTitle: string;
  smallImageUrl?: string;
  theater?: Theater;
  trailer?: MovieTrailer;
  trailerDataLinkName?: string;
};

const imageSizes = 'object-cover w-full aspect-video desktop-xs:w-[600px] desktop-xs:h-[338px] md:w-full md:h-auto md:rounded-md';

export type MovieMediaItem = {
  largeImageUrl?: string;
  smallImageUrl?: string;
  trailer?: MovieTrailer;
};

export const stillImageToMovieMediaItem = (images: Media[]): MovieMediaItem[] => {
  return images.map((image) => {
    if (!image.url) return null;

    return {
      largeImageUrl: image.url,
      smallImageUrl: image.url,
    };
  }).filter(Boolean) as MovieMediaItem[];
};

export const MovieMediaItemComponent = ({
  dataLinkCategory,
  dataLinkName,
  largeImageUrl,
  moviePoster,
  movieTitle,
  smallImageUrl,
  trailer,
  trailerDataLinkName,
}: MovieMediaItemComponentProps): React.JSX.Element => {
  const now = useMemo(() => new Date(), []);
  const searchParams = useSearchParams();
  const showDateParam = searchParams.get('showDate');
  const { data: sharedState } = useSharedState();
  const zipCode = sharedState?.lastTextSearch?.toString();
  const selectedDate = useMemo(
    () => parseDateString(showDateParam) ?? getTodayInTimeZone(),
    [showDateParam]
  );
  const {
    data: previewShowtimesResponse,
    dataUpdatedAt: previewShowtimesUpdatedAt,
    isFetching: isMovieShowtimesFetching,
    isLoading: isMovieShowtimesLoading,
  } = useMovieShowtimes({
    date: selectedDate,
    movieId: moviePoster.cinemarkMovieId,
    pageSize: movieShowtimesTheaterLimits.preview,
    zipCode,
  });
  const {
    data: fullListShowtimesResponse,
    dataUpdatedAt: fullListShowtimesUpdatedAt,
  } = useMovieShowtimes({
    date: selectedDate,
    enabled: false,
    movieId: moviePoster.cinemarkMovieId,
    pageSize: movieShowtimesTheaterLimits.fullList,
    zipCode,
  });
  const movieShowtimesResponse = fullListShowtimesUpdatedAt >= previewShowtimesUpdatedAt
    ? fullListShowtimesResponse ?? previewShowtimesResponse
    : previewShowtimesResponse;
  const isShowtimesLoading = !!zipCode
    && !movieShowtimesResponse
    && (isMovieShowtimesLoading || isMovieShowtimesFetching);

  const selectedTheater = useMemo(
    () => selectEligibleMovieShowtimeTheater(movieShowtimesResponse?.theaters ?? []),
    [movieShowtimesResponse?.theaters]
  );
  const activeShowtimeGroups = useMemo(
    () => selectedTheater
      ? filterActiveTheaterShowtimeGroups(selectedTheater.showtimesByPrintType, { offsetMinutes: 0 })
      : [],
    [selectedTheater]
  );

  const flattenedShowtimes = useMemo(() =>
    activeShowtimeGroups
      .flatMap(group => group.showtimes)
      .filter(showtime => isActiveStatus(showtime.status))
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
  [activeShowtimeGroups]);

  const hasActiveShowtimes = useMemo(() =>
    hasAnyActiveTheaterShowtime(activeShowtimeGroups),
  [activeShowtimeGroups]);

  const movieReleaseDate = moviePoster.movieReleaseDate;

  const hasPresaleShowtimes = movieShowtimesResponse?.hasPresaleShowtimes
    ?? (!!moviePoster.publicOnSaleDate && moviePoster.publicOnSaleDate > now);

  const isBeforeFirstShowDate = !hasActiveShowtimes && !hasPresaleShowtimes
    && !!movieReleaseDate && movieReleaseDate > now;

  const preFirstShowtimeMessage = useMemo(() => {
    if (!isBeforeFirstShowDate) return undefined;
    return buildPreFirstShowtimeMessage(movieTitle, movieReleaseDate!);
  }, [isBeforeFirstShowDate, movieReleaseDate, movieTitle]);

  const presaleMessage = useMemo(() => {
    if (movieShowtimesResponse?.hasPresaleShowtimes) {
      return buildPresaleMessage(movieTitle, {
        presaleDateTimeAppender: movieShowtimesResponse.presaleDateTimeAppender,
        presaleLinkText: movieShowtimesResponse.presaleLinkText,
        presaleLinkUrl: movieShowtimesResponse.presaleLinkUrl,
        publicOnSaleDate: movieShowtimesResponse.publicOnSaleDate,
      });
    }
    if (!movieShowtimesResponse && !!moviePoster.publicOnSaleDate && moviePoster.publicOnSaleDate > now) {
      return buildPresaleMessage(movieTitle, { publicOnSaleDate: moviePoster.publicOnSaleDate });
    }
    return undefined;
  }, [moviePoster.publicOnSaleDate, movieShowtimesResponse, movieTitle, now]);

  const effectiveMoviePoster = useMemo(() => ({
    ...moviePoster,
    theaterId: selectedTheater?.theaterId ?? moviePoster.theaterId,
    theaterName: selectedTheater?.theaterName ?? moviePoster.theaterName,
  }), [moviePoster, selectedTheater]);

  const trailerUrl = trailer?.highResUrl || trailer?.lowResUrl;

  const imageElement =
    <picture>
      <source media={imageMediaViewPorts.minMediumLarge}
        srcSet={largeImageUrl}
      />
      <img alt={`${trailerUrl ? 'Trailer' : 'Still'} for ${movieTitle}`}
        className={imageSizes}
        srcSet={smallImageUrl}
      />
    </picture>
  ;

  if (!trailerUrl) {
    return imageElement;
  }

  return (
    <div className={clsx('relative', imageSizes)}>
      {imageElement}

      <TrailerDialogComponent closeOnBrowseShowtimes
        hideMessageWithNoShowtimes
        dataLinkCategory={dataLinkCategory}
        dataLinkName={trailerDataLinkName}
        firstShowDate={movieReleaseDate}
        hasActiveShowtimes={hasActiveShowtimes}
        hasPresaleShowtimes={hasPresaleShowtimes}
        isBeforeFirstShowDate={isBeforeFirstShowDate}
        isShowtimesLoading={isShowtimesLoading}
        movieDataLinkName={dataLinkName}
        moviePoster={effectiveMoviePoster}
        preFirstShowtimeMessage={preFirstShowtimeMessage}
        presaleLinkText={movieShowtimesResponse?.presaleLinkText}
        presaleLinkUrl={movieShowtimesResponse?.presaleLinkUrl}
        presaleMessage={presaleMessage}
        publicOnSaleDate={movieShowtimesResponse?.publicOnSaleDate ?? moviePoster.publicOnSaleDate}
        showtimes={flattenedShowtimes}
        theaterName={selectedTheater?.theaterName}
        timeZone={selectedTheater?.timeZone}
      >
        <PlayButton ariaLabel="Play Trailer"
          dataTest="button-play"
        />
      </TrailerDialogComponent>
    </div>
  );
};
